/**
 * Single source of truth for the locales the admin dashboard supports.
 *
 * Direction lives here rather than inside the translation files so that
 * `src/locales/*.json` stay pure message catalogues (an `isRTL` entry in there
 * shows up as a translatable key, which it is not).
 */

export const DEFAULT_LOCALE = 'en'

/** localStorage key. Kept as `lang` for backwards compatibility with existing sessions. */
export const LOCALE_STORAGE_KEY = 'lang'

export const SUPPORTED_LOCALES = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    dir: 'ltr',
    // BCP-47 tag used for Intl date/number formatting.
    intlTag: 'en-AE',
  },
  {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    dir: 'rtl',
    intlTag: 'ar-AE',
  },
  // Urdu ('ur') is intentionally not listed here — hidden from the language
  // switcher per request, not deleted. src/locales/ur.json and its messages
  // registration in libs/i18n/index.js are left in place; anyone previously
  // on 'ur' just falls back to DEFAULT_LOCALE via isSupportedLocale() below.
]

export const LOCALE_CODES = SUPPORTED_LOCALES.map(locale => locale.code)

export const isSupportedLocale = code => LOCALE_CODES.includes(code)

/** Never returns undefined — falls back to the default locale config. */
export const getLocaleConfig = code => SUPPORTED_LOCALES.find(locale => locale.code === code)
  || SUPPORTED_LOCALES.find(locale => locale.code === DEFAULT_LOCALE)

export const isRTLLocale = code => getLocaleConfig(code).dir === 'rtl'

const readStoredLocale = () => {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch (error) {
    // Private browsing / disabled storage — fall through to detection.
    return null
  }
}

const detectBrowserLocale = () => {
  if (typeof navigator === 'undefined') return null
  const candidates = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language]

  return candidates
    .filter(Boolean)
    .map(tag => String(tag).toLowerCase().split('-')[0])
    .find(isSupportedLocale) || null
}

/**
 * Resolution order: previously chosen locale → browser preference → default.
 * Always returns a supported locale code, so callers never have to null-check.
 */
export const resolveInitialLocale = () => {
  const stored = readStoredLocale()
  if (isSupportedLocale(stored)) return stored
  return detectBrowserLocale() || DEFAULT_LOCALE
}

export const persistLocale = code => {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, code)
  } catch (error) {
    // Persistence is best-effort; the in-memory locale is still correct.
  }
}

/**
 * Reflects the active locale onto <html> so CSS, screen readers and the
 * browser's own text handling all agree on language and direction.
 */
export const applyDocumentLocale = code => {
  if (typeof document === 'undefined') return
  const { dir } = getLocaleConfig(code)
  const root = document.documentElement

  root.setAttribute('lang', code)
  root.setAttribute('dir', dir)

  // The copy on screen is already in the language the user picked, so browser
  // machine-translation must not run on top of it. Setting `lang` correctly is
  // exactly what prompts Chrome to translate a page whose language differs from
  // the browser's, which turned a correctly-served Urdu page into machine
  // English while keeping the RTL layout. `translate="no"` is the standard
  // opt-out; public/index.html carries the Chrome-specific meta as well.
  root.setAttribute('translate', 'no')

  root.classList.toggle('rtl', dir === 'rtl')
  root.classList.toggle('ltr', dir === 'ltr')
}
